import { useEffect } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { ArrowLeft } from "lucide-react"
import { useGoal, useCreateGoal, useUpdateGoal } from "@/hooks/use-goals"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Skeleton } from "@/components/ui/skeleton"
import { toast } from "sonner"

const goalSchema = z.object({
  name: z.string().min(1, "Nazwa jest wymagana"),
  description: z.string().optional(),
  metric_type: z.string().min(1, "Wybierz metrykę"),
  period: z.string().min(1, "Wybierz okres"),
  scope: z.string().min(1, "Wybierz zakres"),
  target_value: z.coerce.number().positive("Wartość musi być większa od 0"),
  current_value: z.coerce.number().min(0).optional(),
  start_date: z.string().min(1, "Podaj datę rozpoczęcia"),
  end_date: z.string().min(1, "Podaj datę zakończenia"),
  status: z.string().optional(),
})

type GoalFormData = z.infer<typeof goalSchema>

const METRIC_OPTIONS = [
  { value: "deals_won", label: "Wygrane transakcje", unit: "szt." },
  { value: "deals_value", label: "Wartość transakcji", unit: "PLN" },
  { value: "activities_count", label: "Liczba aktywności", unit: "szt." },
  { value: "tasks_completed", label: "Ukończone zadania", unit: "szt." },
  { value: "revenue", label: "Przychód", unit: "PLN" },
  { value: "calls_made", label: "Wykonane telefony", unit: "szt." },
  { value: "meetings_held", label: "Spotkania", unit: "szt." },
  { value: "new_contacts", label: "Nowe kontakty", unit: "szt." },
  { value: "new_companies", label: "Nowi kontrahenci", unit: "szt." },
  { value: "custom", label: "Własna metryka", unit: "" },
]

const PERIOD_OPTIONS = [
  { value: "weekly", label: "Tygodniowy" },
  { value: "monthly", label: "Miesięczny" },
  { value: "quarterly", label: "Kwartalny" },
  { value: "yearly", label: "Roczny" },
  { value: "custom", label: "Własny okres" },
]

export function GoalFormPage() {
  const navigate = useNavigate()
  const { id } = useParams()
  const isEditing = !!id

  const { data: goalData, isLoading: isLoadingGoal } = useGoal(id || "")
  const createGoal = useCreateGoal()
  const updateGoal = useUpdateGoal()

  const form = useForm<GoalFormData>({
    resolver: zodResolver(goalSchema),
    defaultValues: {
      name: "",
      description: "",
      metric_type: "",
      period: "monthly",
      scope: "individual",
      target_value: 0,
      current_value: 0,
      start_date: new Date().toISOString().split("T")[0],
      end_date: "",
      status: "active",
    },
  })

  useEffect(() => {
    if (goalData?.data) {
      const g = goalData.data
      form.reset({
        name: g.name,
        description: g.description || "",
        metric_type: g.metric_type,
        period: g.period,
        scope: g.scope,
        target_value: g.target_value,
        current_value: g.current_value,
        start_date: g.start_date,
        end_date: g.end_date,
        status: g.status,
      })
    }
  }, [goalData])

  const onSubmit = (data: GoalFormData) => {
    if (isEditing) {
      updateGoal.mutate(
        { id: id!, data },
        {
          onSuccess: () => {
            toast.success("Cel zaktualizowany")
            navigate(`/goals/${id}`)
          },
          onError: () => toast.error("Błąd zapisywania celu"),
        }
      )
    } else {
      createGoal.mutate(data, {
        onSuccess: (res) => {
          toast.success("Cel utworzony")
          navigate(`/goals/${res.data.id}`)
        },
        onError: () => toast.error("Błąd tworzenia celu"),
      })
    }
  }

  const handlePeriodChange = (period: string) => {
    form.setValue("period", period)
    const today = new Date()
    let startDate = ""
    let endDate = ""

    switch (period) {
      case "weekly": {
        const day = today.getDay()
        const monday = new Date(today)
        monday.setDate(today.getDate() - (day === 0 ? 6 : day - 1))
        const sunday = new Date(monday)
        sunday.setDate(monday.getDate() + 6)
        startDate = monday.toISOString().split("T")[0]
        endDate = sunday.toISOString().split("T")[0]
        break
      }
      case "monthly": {
        startDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-01`
        const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0)
        endDate = lastDay.toISOString().split("T")[0]
        break
      }
      case "quarterly": {
        const q = Math.floor(today.getMonth() / 3)
        startDate = `${today.getFullYear()}-${String(q * 3 + 1).padStart(2, "0")}-01`
        const lastDayQ = new Date(today.getFullYear(), q * 3 + 3, 0)
        endDate = lastDayQ.toISOString().split("T")[0]
        break
      }
      case "yearly": {
        startDate = `${today.getFullYear()}-01-01`
        endDate = `${today.getFullYear()}-12-31`
        break
      }
    }

    if (startDate) form.setValue("start_date", startDate)
    if (endDate) form.setValue("end_date", endDate)
  }

  if (isEditing && isLoadingGoal) {
    return (
      <div className="p-6 max-w-[720px] mx-auto space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-[400px]" />
      </div>
    )
  }

  return (
    <div className="p-6 max-w-[720px] mx-auto">
      <Button variant="ghost" size="sm" className="mb-4" onClick={() => navigate(-1)}>
        <ArrowLeft className="h-4 w-4 mr-1" />
        Wróć
      </Button>

      <h1 className="page-title text-2xl mb-6">
        {isEditing ? "Edytuj cel" : "Nowy cel"}
      </h1>

      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <div className="space-y-4">
          <div>
            <Label htmlFor="name">Nazwa celu *</Label>
            <Input id="name" {...form.register("name")} placeholder="np. Wygraj 10 transakcji w Q1" />
            {form.formState.errors.name && (
              <p className="text-sm text-red-500 mt-1">{form.formState.errors.name.message}</p>
            )}
          </div>

          <div>
            <Label htmlFor="description">Opis</Label>
            <Textarea id="description" {...form.register("description")} placeholder="Opcjonalny opis celu..." rows={3} />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Metryka *</Label>
              <Select
                value={form.watch("metric_type")}
                onValueChange={(v) => form.setValue("metric_type", v)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Wybierz metrykę" />
                </SelectTrigger>
                <SelectContent>
                  {METRIC_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {form.formState.errors.metric_type && (
                <p className="text-sm text-red-500 mt-1">{form.formState.errors.metric_type.message}</p>
              )}
            </div>

            <div>
              <Label>Okres *</Label>
              <Select
                value={form.watch("period")}
                onValueChange={handlePeriodChange}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Wybierz okres" />
                </SelectTrigger>
                <SelectContent>
                  {PERIOD_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Zakres</Label>
              <Select
                value={form.watch("scope")}
                onValueChange={(v) => form.setValue("scope", v)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="individual">Indywidualny</SelectItem>
                  <SelectItem value="team">Zespołowy</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="target_value">Wartość docelowa *</Label>
              <Input
                id="target_value"
                type="number"
                step="any"
                {...form.register("target_value")}
              />
              {form.formState.errors.target_value && (
                <p className="text-sm text-red-500 mt-1">{form.formState.errors.target_value.message}</p>
              )}
            </div>
          </div>

          {isEditing && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="current_value">Bieżąca wartość</Label>
                <Input
                  id="current_value"
                  type="number"
                  step="any"
                  {...form.register("current_value")}
                />
              </div>
              <div>
                <Label>Status</Label>
                <Select
                  value={form.watch("status")}
                  onValueChange={(v) => form.setValue("status", v)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Aktywny</SelectItem>
                    <SelectItem value="completed">Osiągnięty</SelectItem>
                    <SelectItem value="failed">Nieosiągnięty</SelectItem>
                    <SelectItem value="cancelled">Anulowany</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="start_date">Data rozpoczęcia *</Label>
              <Input id="start_date" type="date" {...form.register("start_date")} />
              {form.formState.errors.start_date && (
                <p className="text-sm text-red-500 mt-1">{form.formState.errors.start_date.message}</p>
              )}
            </div>
            <div>
              <Label htmlFor="end_date">Data zakończenia *</Label>
              <Input id="end_date" type="date" {...form.register("end_date")} />
              {form.formState.errors.end_date && (
                <p className="text-sm text-red-500 mt-1">{form.formState.errors.end_date.message}</p>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3 pt-4 border-t">
          <Button type="submit" disabled={createGoal.isPending || updateGoal.isPending}>
            {createGoal.isPending || updateGoal.isPending ? "Zapisywanie..." : isEditing ? "Zapisz zmiany" : "Utwórz cel"}
          </Button>
          <Button type="button" variant="outline" onClick={() => navigate(-1)}>
            Anuluj
          </Button>
        </div>
      </form>
    </div>
  )
}
